import { InsightSynthesis } from '../../../../../types/insights-synthesis';
import { InsightsFilter } from '../hooks/useInsightsFilters';

// Get priority color (for scatter plot visualization)
export const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
      return 'success';
    default:
      return 'secondary';
  }
};

// Icon mapping for severity
export const getSeverityIcon = (severity: string): { icon: string; color: string } => {
  switch (severity) {
    case 'critical':
      return { icon: 'fa-circle', color: '#dc3545' };
    case 'warning':
      return { icon: 'fa-circle', color: '#fd7e14' };
    default:
      return { icon: 'fa-circle', color: '#6c757d' };
  }
};

// Get pillar icon
export const getPillarIcon = (sectionType: string): string => {
  const icons: Record<string, string> = {
    all: 'fa-infinity',
    environment: 'fa-leaf',
    social: 'fa-users',
    governance: 'fa-balance-scale',
    economic: 'fa-chart-line',
  };
  return icons[sectionType] || 'fa-circle';
};

// Get time horizon icon
export const getTimeHorizonIcon = (timeHorizon: string): string => {
  const icons: Record<string, string> = {
    immediate: 'fa-bolt',
    short_term: 'fa-clock',
    medium_term: 'fa-clock',
    long_term: 'fa-calendar',
  };
  return icons[timeHorizon] || 'fa-clock';
};

// Get control level icon
export const getControlLevelIcon = (level: string): string => {
  const icons: Record<string, string> = {
    low: 'fa-hand-paper',
    medium: 'fa-hand-rock',
    high: 'fa-hand-pointer',
  };
  return icons[level] || 'fa-hand';
};

// Get impact icon and color
export const getImpactInfo = (impact: string): { icon: string; color: string } => {
  const info: Record<string, { icon: string; color: string }> = {
    critical: { icon: 'fa-exclamation-triangle', color: 'danger' },
    high: { icon: 'fa-arrow-up', color: 'warning' },
    medium: { icon: 'fa-minus', color: 'info' },
    low: { icon: 'fa-arrow-down', color: 'secondary' },
  };
  return info[impact] || { icon: 'fa-circle', color: 'secondary' };
};

// Get priority level text and color from numeric score
export const getPriorityInfo = (score: number): { label: string; color: string } => {
  if (score === 10) return { label: 'Critical', color: 'danger' };
  if (score >= 7) return { label: 'High', color: 'warning' };
  if (score >= 5) return { label: 'Medium', color: 'info' };
  if (score >= 3) return { label: 'Low', color: 'success' };
  return { label: 'Very Low', color: 'secondary' };
};

// Get SWOT icon
export const getSwotIcon = (swotCategory: string): string => {
  const icons: Record<string, string> = {
    all: 'fa-infinity',
    strength: 'fa-shield-alt',
    weakness: 'fa-exclamation-triangle',
    opportunity: 'fa-lightbulb',
    threat: 'fa-skull-crossbones',
  };
  return icons[swotCategory] || 'fa-circle';
};

// Get severity level and color based on value
export const getSeverityInfo = (value: number): { level: string; colorClass: string; gradient: string } => {
  if (value === 10) {
    return {
      level: 'Critical',
      colorClass: 'danger',
      gradient: 'linear-gradient(90deg, var(--theme-DangerLight) 0%, var(--theme-DangerDark) 50%)',
    };
  }
  if (value >= 8) {
    return {
      level: 'High',
      colorClass: 'warning',
      gradient: 'linear-gradient(90deg, var(--theme-DangerExtralight) 0%, var(--theme-DangerMedium) 50%)',
    };
  }
  if (value >= 5) {
    return {
      level: 'Medium',
      colorClass: 'info',
      gradient: 'linear-gradient(90deg, var(--theme-WarningExtralight) 0%, var(--theme-WarningMedium) 50%)',
    };
  }
  if (value >= 3) {
    return {
      level: 'Low',
      colorClass: 'success',
      gradient: 'linear-gradient(90deg, var(--theme-SuccessExtralight) 0%, var(--theme-SuccessDark) 50%)',
    };
  }
  return {
    level: 'Very Low',
    colorClass: 'secondary',
    gradient: 'linear-gradient(90deg, var(--theme-TextLight) 0%, var(--theme-TextDark) 50%)',
  };
};

/**
 * Filter insights based on pillar and SWOT category
 */
export const filterInsights = (
  insights: InsightSynthesis[],
  filters: InsightsFilter
): InsightSynthesis[] => {
  return insights.filter((insight) => {
    // Filter by pillar (section type)
    if (filters.pillar !== 'all' && insight.source.metadata.sectionType !== filters.pillar) {
      return false;
    }

    // Filter by SWOT category
    if (filters.swot !== 'all' && insight.content.swotCategory !== filters.swot) {
      return false;
    }

    return true;
  });
};
